import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, ShieldCheck, ShieldAlert, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const V1 = `${BACKEND}/api/v1`;

const StatusBanner = ({ status }) => {
  const map = {
    true: { label: "VERIFIED — TRUE", cls: "bg-truth text-white", Icon: ShieldCheck },
    partial: { label: "PARTIAL TRUTH", cls: "bg-telemetry text-white", Icon: ShieldAlert },
    false: { label: "FLAGGED — FALSE", cls: "bg-anomaly text-white", Icon: ShieldAlert },
  };
  const s = map[status] || map.partial;
  return (
    <div data-testid="verify-status-banner" className={`${s.cls} px-5 py-4 flex items-center gap-3`}>
      <s.Icon className="w-5 h-5" />
      <span className="font-mono text-sm tracking-[0.2em] font-semibold">{s.label}</span>
    </div>
  );
};

const Metric = ({ label, value, warn }) => (
  <div className="border border-border px-4 py-3">
    <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">{label}</p>
    <p className={`font-mono text-lg font-semibold mt-1 ${warn ? "text-anomaly" : "text-foreground"}`}>{value}</p>
  </div>
);

const Playground = ({ compact = false }) => {
  const [mode, setMode] = useState("sandbox");
  const [apiKey, setApiKey] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [selected, setSelected] = useState(null);
  const [payload, setPayload] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const base = mode === "real" ? V1 : API;
  const authHeaders = mode === "real" ? { "X-GeoVerify-Key": apiKey } : {};

  useEffect(() => {
    axios.get(`${API}/event/scenarios`).then((res) => {
      setScenarios(res.data.scenarios);
      if (res.data.scenarios.length) {
        setSelected(res.data.scenarios[0]);
        setPayload(JSON.stringify(res.data.scenarios[0].event, null, 2));
      }
    }).catch(() => setError("Could not load scenarios"));
  }, []);

  const pick = (s) => {
    setSelected(s);
    setPayload(JSON.stringify(s.event, null, 2));
    setResult(null);
    setError(null);
  };

  const switchMode = (m) => {
    setMode(m);
    setResult(null);
    setError(null);
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const event = JSON.parse(payload);
      const res = await axios.post(`${base}/event/verify`, { event }, { headers: authHeaders });
      setResult(res.data);
    } catch (e) {
      const status = e.response?.status;
      setError(
        status === 401
          ? "REAL MODE — invalid or missing API key"
          : e.response?.data?.detail || e.message || "Verification failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const exportPacket = async (format = "json") => {
    if (!result) return;
    setExporting(true);
    try {
      const suffix = format === "pdf" ? "export.pdf" : "export";
      const res = await fetch(`${base}/event/audit/${result.event_id}/${suffix}`, { headers: authHeaders });
      if (!res.ok) throw new Error(res.status === 401 ? "Invalid or missing API key" : "Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `geoverify-audit-${result.event_id}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="api-playground" className="border border-border bg-card">
      <div className="border-b border-border px-5 py-3 flex items-center justify-between bg-muted/50 gap-4 flex-wrap">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
          {mode === "real" ? "POST /api/v1/event/verify — Real Mode" : "POST /api/event/verify — Live Sandbox"}
        </p>
        <div className="flex items-center gap-3">
          <div className="flex border border-border">
            <button
              data-testid="mode-sandbox-tab"
              onClick={() => switchMode("sandbox")}
              className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 transition-colors duration-200 ${
                mode === "sandbox" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Sandbox
            </button>
            <button
              data-testid="mode-real-tab"
              onClick={() => switchMode("real")}
              className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-1.5 transition-colors duration-200 ${
                mode === "real" ? "bg-telemetry text-white" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Real Mode
            </button>
          </div>
          <span className="font-mono text-[10px] px-2 py-1 border border-border text-muted-foreground">
            {mode === "real" ? "KEY REQUIRED" : "MOCK DATA"}
          </span>
        </div>
      </div>
      {mode === "real" && (
        <div className="border-b border-border px-5 py-4 flex items-center gap-4 flex-wrap bg-muted/30">
          <label htmlFor="pg-api-key" className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
            X-GeoVerify-Key
          </label>
          <input
            id="pg-api-key"
            data-testid="api-key-input"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="gvk_…"
            className="flex-1 min-w-[220px] bg-card border border-border px-4 py-2 font-mono text-xs focus:outline-none focus:border-telemetry transition-colors duration-200"
          />
        </div>
      )}
      <div className="p-5 border-b border-border flex flex-wrap gap-2">
        {scenarios.map((s) => (
          <button
            key={s.id}
            data-testid={`scenario-${s.id}`}
            onClick={() => pick(s)}
            className={`font-mono text-[11px] tracking-wider uppercase px-4 py-2 border transition-colors duration-200 ${
              selected?.id === s.id
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {selected && (
        <p data-testid="scenario-description" className="px-5 pt-4 text-sm text-muted-foreground">
          {selected.description}
        </p>
      )}
      <div className={`grid ${compact ? "" : "lg:grid-cols-2"} divide-x divide-border`}>
        <div className="p-5">
          <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-3">
            Request Payload — editable
          </p>
          <textarea
            data-testid="playground-payload-input"
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            spellCheck={false}
            className="w-full h-[380px] bg-[#0A0A0A] text-[#9AE6B4] font-mono text-xs leading-relaxed p-4 resize-none focus:outline-none focus:ring-1 focus:ring-telemetry"
          />
          <div className="flex gap-3 mt-4">
            <button
              data-testid="run-verification-btn"
              onClick={run}
              disabled={loading}
              className="bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase px-6 py-3 flex items-center gap-2 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
            >
              <Play className="w-3.5 h-3.5" /> {loading ? "Verifying…" : "Run Verification"}
            </button>
            <button
              data-testid="reset-payload-btn"
              onClick={() => selected && pick(selected)}
              className="border border-border font-mono text-xs tracking-[0.2em] uppercase px-6 py-3 flex items-center gap-2 hover:border-foreground transition-colors duration-200"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
          {error && (
            <p data-testid="playground-error" className="mt-4 font-mono text-xs text-anomaly border border-anomaly px-4 py-3">
              ERROR — {error}
            </p>
          )}
        </div>
        <div className={compact ? "border-t border-border" : ""}>
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key={result.audit_signature}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                data-testid="verify-result"
              >
                <StatusBanner status={result.truth_status} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-5">
                  <Metric label="Distance Δ" value={`${result.distance_delta_m} m`} warn={result.distance_delta_m > 150} />
                  <Metric label="Time Δ" value={`${result.time_delta_minutes} min`} warn={Math.abs(result.time_delta_minutes) > 10} />
                  <Metric label="Telemetry" value={result.telemetry_score.toFixed(2)} warn={result.telemetry_score < 0.8} />
                  <Metric label="Claim" value={result.claim_consistency_score.toFixed(2)} warn={result.claim_consistency_score < 1} />
                </div>
                <div className="px-5 pb-2">
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-2">Compliance Flags</p>
                  <div data-testid="compliance-flags" className="flex flex-wrap gap-2">
                    {result.compliance_flags.map((f) => (
                      <span
                        key={f}
                        className={`font-mono text-[10px] tracking-wider px-2.5 py-1 border ${
                          /missed|exceeded|breach|missing/.test(f)
                            ? "border-anomaly text-anomaly"
                            : "border-truth text-truth"
                        }`}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="px-5 py-3">
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-2">Anomaly Signals</p>
                  <div data-testid="anomaly-tags" className="flex flex-wrap gap-2">
                    {result.anomaly_tags.length ? (
                      result.anomaly_tags.map((t) => (
                        <span key={t} className="font-mono text-[10px] tracking-wider px-2.5 py-1 bg-anomaly text-white">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 bg-truth text-white">NONE DETECTED</span>
                    )}
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <p className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase mb-2">Audit Signature · SHA-256 Chain</p>
                  <p data-testid="audit-signature" className="font-mono text-[11px] bg-muted px-4 py-3 break-all text-foreground">
                    {result.audit_signature}
                  </p>
                  <div className="mt-4 flex gap-3 flex-wrap">
                    <button
                      data-testid="export-audit-btn"
                      onClick={() => exportPacket("json")}
                      disabled={exporting}
                      className="border border-foreground font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 flex items-center gap-2 hover:bg-foreground hover:text-background transition-colors duration-200 disabled:opacity-50"
                    >
                      <Download className="w-3.5 h-3.5" /> {exporting ? "Exporting…" : "Export JSON Packet"}
                    </button>
                    <button
                      data-testid="export-pdf-btn"
                      onClick={() => exportPacket("pdf")}
                      disabled={exporting}
                      className="bg-foreground text-background font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 flex items-center gap-2 hover:bg-telemetry transition-colors duration-200 disabled:opacity-50"
                    >
                      <Download className="w-3.5 h-3.5" /> {exporting ? "Exporting…" : "Export PDF for Insurer"}
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="h-full min-h-[300px] flex items-center justify-center p-8"
              >
                <p data-testid="playground-idle" className="font-mono text-xs text-muted-foreground tracking-widest text-center leading-loose">
                  SELECT A SCENARIO AND RUN VERIFICATION
                  <br />
                  TRUTH · COMPLIANCE · ANOMALY · AUDIT
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Playground;
